import React, {useEffect, useState} from 'react';
import {inject, observer} from 'mobx-react';
import CourseAnalyticsCommentsUI from '../view/CourseAnalyticsCommentsUI';
import {useOutletContext, useParams} from 'react-router-dom';
import CourseAnalyticsCommentsStore from '../store/CourseAnalyticsCommentsStore';
import {CourseComment} from '../../../../../data/models/Comments/CommentModel';
import {GradebookLesson} from '../../../../../data/models/Course/Gradebook';
import {downloadCSV} from '../../../../../utils/downloadCsv';
import {analyticsText} from '../../../analyticsText';
import {formatDateTime} from '../../../../../utils/notificationTimeTextConvertor';

interface Props {
  courseAnalyticsCommentsStore?: CourseAnalyticsCommentsStore;
}

const CourseAnalyticsCommentsContainer = inject('courseAnalyticsCommentsStore')(
  observer(({courseAnalyticsCommentsStore}: Props) => {
    const [comments, setComments] = useState<CourseComment[]>([]);
    const [microreels, setMicroreels] = useState<GradebookLesson[]>([]);
    // True until the first comments response arrives (success or error).
    const [commentsLoading, setCommentsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const {organizationId, courseId} = useParams();

    const [, courseName] = useOutletContext<[boolean, string, number]>();

    useEffect(() => {
      if (!organizationId || !courseId) return;
      courseAnalyticsCommentsStore!
        .getCourseMicroreels(organizationId, courseId)
        .then(lessons => {
          setMicroreels(lessons);
          return courseAnalyticsCommentsStore!.getComments(courseId, lessons);
        })
        .then(data => {
          setComments(data);
          setCommentsLoading(false);
        });
    }, [organizationId, courseId]);

    const getMicroreelTitle = (comment: CourseComment) =>
      microreels.find(lesson => lesson.lessonId === comment.microreelId)?.title;

    const onExport = () => {
      if (!comments.length) return;
      downloadCSV(
        [
          [
            analyticsText.author,
            analyticsText.comment,
            analyticsText.microreel,
            analyticsText.date,
          ],
          ...comments.map(comment => [
            comment.userName || '',
            comment.text,
            getMicroreelTitle(comment) || '-',
            formatDateTime(comment.dateCreated),
          ]),
        ],
        `${courseName}_comments.csv`,
      );
    };

    return (
      <CourseAnalyticsCommentsUI
        organizationId={organizationId || ''}
        courseId={courseId || ''}
        comments={comments}
        microreels={microreels}
        isLoading={commentsLoading}
        search={search}
        setSearch={setSearch}
        onExport={onExport}
      />
    );
  }),
);

export default CourseAnalyticsCommentsContainer;
